import type { SubscriptionPricingConfig, CustomerEquipmentTypeResponse } from '@/types';

export const MONTHLY_RENTAL_TIERS = [
  { name: 'Flexible', months: 0, field: 'monthlySubscriptionPrice' as const, newItemField: 'newItemMonthlySubscriptionPrice' as const, isNoCommitment: true, description: 'Return anytime' },
  { name: '3+ Months', months: 3, field: 'price3MonthsMonthlyRate' as const, newItemField: 'newItemPrice3MonthsMonthlyRate' as const, isNoCommitment: false, description: 'Best for short stays' },
  { name: '6+ Months', months: 6, field: 'price6MonthsMonthlyRate' as const, newItemField: 'newItemPrice6MonthsMonthlyRate' as const, isNoCommitment: false, description: 'Great value' },
  { name: '12+ Months', months: 12, field: 'price12MonthsMonthlyRate' as const, newItemField: 'newItemPrice12MonthsMonthlyRate' as const, isNoCommitment: false, description: 'Best value' },
] as const;

export type MonthlyRentalTierField = typeof MONTHLY_RENTAL_TIERS[number]['field'];

export function hasMonthlyRentalPricing(pricing: SubscriptionPricingConfig): boolean {
  return getAvailableMonthlyRentalTiers(pricing).length > 0;
}

export function getAvailableMonthlyRentalTiers(
  pricing: SubscriptionPricingConfig,
  product?: CustomerEquipmentTypeResponse,
  isNewItem?: boolean
): Array<{ tierName: string; months: number; monthlyRate: number; totalMinimumCost: number; isNoCommitment: boolean }> {
  return MONTHLY_RENTAL_TIERS
    .map(tier => {
      // If new item pricing, try new item field first, fall back to standard
      let monthlyRate: number | undefined;
      if (isNewItem && product?.allowNewItemRental) {
        monthlyRate = product[tier.newItemField] ?? pricing[tier.field];
      } else {
        monthlyRate = pricing[tier.field];
      }

      if (monthlyRate != null && monthlyRate > 0) {
        const billingMonths = tier.months === 0 ? 1 : tier.months;
        return {
          tierName: tier.name,
          months: tier.months,
          monthlyRate,
          totalMinimumCost: monthlyRate * billingMonths,
          isNoCommitment: tier.isNoCommitment,
        };
      }
      return null;
    })
    .filter((t): t is NonNullable<typeof t> => t !== null);
}

export function getLowestMonthlyRentalRate(pricing: SubscriptionPricingConfig): number | null {
  // Include the no-commitment "Flexible" tier (monthlySubscriptionPrice) — products
  // that only offer Flexible monthly rental (no 3/6/12-month rates) otherwise yield
  // null here and the product header rendered "from €0.00/month". Mirrors the fields
  // considered by getAvailableMonthlyRentalTiers.
  const rates = [
    pricing.monthlySubscriptionPrice,
    pricing.price3MonthsMonthlyRate,
    pricing.price6MonthsMonthlyRate,
    pricing.price12MonthsMonthlyRate,
  ].filter((p): p is number => p != null && p > 0);

  return rates.length > 0 ? Math.min(...rates) : null;
}
